import React from 'react';
import { motion } from 'framer-motion';
import { Star, ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const GoogleReviews = () => {
  const { language } = useLanguage();
  const googleReviewsUrl = 'https://www.google.com/maps/search/Beni+Gipsergesch%C3%A4ft+GmbH+Aesch';

  const reviews = [
    {
      name: 'Thomas M.',
      rating: 5,
      text: language === 'de' ? 'Ausgezeichnete Gipserarbeiten! Das Team war professionell und pünktlich. Sehr zu empfehlen!' : language === 'en' ? 'Excellent plastering work! Professional and punctual. Highly recommended!' : 'Excellents travaux de plâtrerie! Professionnel et ponctuel!',
      date: '2025'
    },
    {
      name: 'Sandra K.',
      rating: 5,
      text: language === 'de' ? 'Wunderschöne Deckenarbeiten. Sehr saubere und fachgerechte Arbeit!' : language === 'en' ? 'Beautiful ceiling work. Very clean and professional!' : 'Beaux travaux de plafond. Très propre et professionnel!',
      date: '2025'
    },
    {
      name: 'Peter H.',
      rating: 5,
      text: language === 'de' ? 'Super Fassadensanierung und top Qualität. Fachgerecht, zuverlässig und preiswert – genau wie versprochen.' : language === 'en' ? 'Great facade renovation and top quality. Professional, reliable and affordable.' : 'Excellente rénovation de façade. Professionnel, fiable et abordable!',
      date: '2024'
    }
  ];

  const content = {
    de: { title: 'Was unsere Kunden sagen', subtitle: 'Fachgerecht. Zuverlässig. Preiswert.', cta: 'Bewertung schreiben', viewAll: 'Alle Bewertungen' },
    en: { title: 'What Our Customers Say', subtitle: 'Professional. Reliable. Affordable.', cta: 'Write a Review', viewAll: 'All Reviews' },
    fr: { title: 'Ce que disent nos clients', subtitle: 'Professionnel. Fiable. Abordable.', cta: 'Écrire un avis', viewAll: 'Tous les avis' }
  };

  const t = content[language] || content.de;

  return (
    <section className="py-20 bg-white" data-testid="google-reviews-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-12">
          {/* Google Badge with real Google button */}
          <a
            href={googleReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-3 bg-white border border-gray-200 px-6 py-3 rounded-full shadow-sm mb-6 hover:shadow-md transition-shadow cursor-pointer"
            data-testid="google-reviews-badge"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <div className="flex items-center space-x-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star key={star} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <span className="text-sm font-semibold text-gray-700">5.0</span>
            <span className="text-xs text-gray-500">Google</span>
          </a>

          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4" data-testid="reviews-title">{t.title}</h2>
          <p className="text-gray-600 max-w-xl mx-auto">{t.subtitle}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {reviews.map((review, index) => (
            <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.1 }} className="bg-[#F8F9FA] p-6 border border-gray-100 hover:shadow-lg transition-shadow" data-testid={`review-card-${index}`}>
              <div className="flex items-center space-x-1 mb-4">
                {[...Array(review.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-700 mb-4 leading-relaxed">"{review.text}"</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-[#1A1A1A]">{review.name}</p>
                  <p className="text-sm text-gray-500">{review.date}</p>
                </div>
                <svg className="w-6 h-6 opacity-50" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3 }} className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a href={googleReviewsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 bg-[#E31C23] text-white px-8 py-4 font-semibold hover:bg-[#B91219] transition-colors duration-300" data-testid="write-review-btn">
            <Star className="w-5 h-5" />
            <span>{t.cta}</span>
          </a>
          <a href={googleReviewsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 border-2 border-[#1A1A1A] text-[#1A1A1A] px-8 py-4 font-semibold hover:bg-[#1A1A1A] hover:text-white transition-colors duration-300" data-testid="view-all-reviews-btn">
            <span>{t.viewAll}</span>
            <ExternalLink className="w-5 h-5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
